import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { authEmitter } from '@cocoonspace/shared/domains/auth/emitters/auth.emitter'
import { Button } from '@cocoonspace/ui/components/button'
import { Form } from '@cocoonspace/ui/components/form'
import { FormControlledInput } from '@cocoonspace/ui/components/form-controlled-input'
import { Input } from '@cocoonspace/ui/components/input'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@cocoonspace/ui/components/popover'
import { Separator } from '@cocoonspace/ui/components/separator'
import { zodResolver } from '@hookform/resolvers/zod'
import { Trans, useLingui } from '@lingui/react/macro'
import { signInWithEmailAndPassword } from 'firebase/auth'
import { CircleHelpIcon } from 'lucide-react'
import { useRouter, useSearchParams } from 'next/navigation'
import * as z from 'zod'

import { useHttpErrorMsg } from '~/lib/get-http-error-msg'
import { getAuthInstance, useAuth } from '~/providers/auth-provider'
import { LastUsedBadge, useLastUsed } from './last-use'
import {
	AppleConnectButton,
	GoogleConnectButton,
	MicrosoftConnectButton,
} from './social-auth-list'

type FormData = z.infer<typeof loginSchema>

const loginSchema = z.object({
	email: z.email().min(1),
	password: z.string().min(1),
})

const LegacyLoginDisclaimer = ({ onClick }: { onClick: () => void }) => {
	const { t } = useLingui()

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					className='w-full'
					size='sm'
					type='button'
					variant='link'
				>
					<div className='flex items-center justify-center gap-x-1.5 text-sm'>
						<CircleHelpIcon size={16} />
						{t`Vous vous êtes inscrit via Facebook ?`}
					</div>
				</Button>
			</PopoverTrigger>

			<PopoverContent
				className='max-w-[320px] text-sm'
				side='top'
			>
				<div className='text-sm'>
					<Trans>
						Utilisez la{' '}
						<Button
							className='text-sm underline-offset-[3px]'
							onClick={onClick}
							size='sm'
							variant='link'
						>
							réinitialisation de mot de passe
						</Button>{' '}
						pour passer en connexion e-mail / mot de passe.
					</Trans>
				</div>
			</PopoverContent>
		</Popover>
	)
}

const NoAccount = () => {
	const { t } = useLingui()
	const { openRegister } = useAuth()

	return (
		<div className='flex flex-row justify-center gap-2 px-6'>
			{t`Vous n'avez pas de compte ?`}

			<button
				type='button'
				className='text-primary'
				data-testid='goto-register-view-button'
				onClick={() => openRegister()}
			>
				{t`S'inscrire`}
			</button>
		</div>
	)
}

export const LoginForm = () => {
	const router = useRouter()
	const searchParams = useSearchParams()
	const httpErrorMsg = useHttpErrorMsg()
	const { t } = useLingui()
	const { lastUsed, markUsed } = useLastUsed()
	const { setIsAuthDialogOpen, setAuthDialogActiveTab } = useAuth()

	const [authError, setAuthError] = useState<string | null>(null)

	const form = useForm<FormData>({
		resolver: zodResolver(loginSchema),
		values: {
			email: '',
			password: '',
		},
	})

	const onSubmitForm = (data: FormData) => {
		const auth = getAuthInstance()

		return signInWithEmailAndPassword(auth, data.email, data.password)
			.then((res) => {
				if (res?.user) {
					const continueUrl = searchParams.get('continueUrl')
					if (continueUrl) {
						router.push(continueUrl)
					}

					authEmitter.emit('login.success')
					markUsed('password')
					setIsAuthDialogOpen(false)
				}
			})
			.catch((error) => {
				setAuthError(error.code)
			})
	}

	return (
		<div className='flex min-h-[200px] flex-col gap-3'>
			<form
				className='flex flex-col gap-y-6 px-6'
				noValidate
				onSubmit={form.handleSubmit(onSubmitForm)}
			>
				<Form {...form}>
					{!!authError && (
						<p className='text-primary mb-4 text-sm'>
							{httpErrorMsg[authError]?.() || t`Une erreur est survenue`}
						</p>
					)}

					<div className='flex flex-col'>
						<FormControlledInput
							control={form.control}
							label={t`Email`}
							name='email'
							render={({ field }) => (
								<Input
									{...field}
									required
									type='email'
								/>
							)}
							required
						/>

						<FormControlledInput
							control={form.control}
							label={t`Mot de passe`}
							name='password'
							render={({ field }) => (
								<Input
									{...field}
									required
									type='password'
								/>
							)}
							required
						/>

						<div className='text-right'>
							<Button
								onClick={() => setAuthDialogActiveTab('forgottenPass')}
								size='sm'
								type='button'
								variant='link'
							>
								{t`🔐 J'ai oublié mon mot de passe`}
							</Button>
						</div>
					</div>

					<div className='flex flex-col gap-y-4'>
						<Button
							type='submit'
							className='relative w-full'
							data-testid='login-btn'
							disabled={form.formState.isSubmitting}
						>
							{t`Se connecter`}
							{lastUsed === 'password' && <LastUsedBadge />}
						</Button>

						<div className='flex flex-row items-center gap-4 leading-none'>
							<Separator className='flex-1' />
							<div className='text-center'>{t`Ou`}</div>
							<Separator className='flex-1' />
						</div>

						<GoogleConnectButton action='login' />
						<AppleConnectButton action='login' />
						<MicrosoftConnectButton action='login' />

						<LegacyLoginDisclaimer
							onClick={() => setAuthDialogActiveTab('forgottenPass')}
						/>
					</div>
				</Form>
			</form>

			<Separator />
			<NoAccount />
		</div>
	)
}
